import { saveAs } from 'file-saver';

// Opening and downloading a stored file with its own name.
//
// A Storage link packs the file's whole path in its last part
// (".../o/<order>%2F<uuid>%2F<name>?alt=media&token=..."), and Chrome's PDF
// viewer names the download after that part: "<order>_<uuid>_<name>" (Alejo,
// 28-oct-2026). So a PDF opens at /archivo/<name>?u=<its Storage link>, which
// the worker in public/archivo/sw.js answers with the file. Other files, and
// links that aren't the site's bucket, open as before.
// Production's bucket answers any origin (CORS *, GET); staging's doesn't, so
// there the worker sends the tab on to the Storage link (the old name).
// Only on a computer's Chrome or Edge, the viewer with that problem: on phones
// a PDF the worker answered showed a black screen (Alejo, 29-oct-2026), so
// there, and in Safari and Firefox, a file opens at its Storage link as it did
// before, and a worker a phone already installed is removed.

const BASE = process.env.REACT_APP_STORAGE_URL || '';
const SCOPE = '/archivo/';
const RETRY_KEY = 'everbary.fileViewRetry';

const matches = (query) => Boolean(window.matchMedia && window.matchMedia(query).matches);

// A phone or a tablet: no mouse.
export const touchDevice = () => typeof window !== 'undefined' && matches('(hover: none) and (pointer: coarse)');

// Chrome or Edge on a computer (userAgentData is Chromium's alone; it says
// mobile on Android phones, and an Android tablet has no fine pointer).
const desktopChromium = () => {
  const data = navigator.userAgentData;
  return Boolean(data && data.mobile === false && Array.isArray(data.brands) &&
    data.brands.some((b) => /Chromium/i.test(b.brand))) &&
    !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) && matches('(pointer: fine)') && !touchDevice();
};

const supported = () =>
  typeof window !== 'undefined' && typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator && window.isSecureContext && Boolean(BASE) && desktopChromium();

const isStored = (url) => typeof url === 'string' && Boolean(BASE) && url.startsWith(BASE + '/o/');

let registration = null;
export const registerFileWorker = () => {
  if (!supported()) return Promise.resolve(null);
  if (!registration) {
    registration = navigator.serviceWorker
      .register(`${SCOPE}sw.js?base=${encodeURIComponent(BASE)}`, { scope: SCOPE })
      .catch(() => null);
  }
  return registration;
};

// Where the worker isn't wanted (a phone that installed it on 28-oct), away.
export const removeFileWorker = () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || supported()) return;
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.filter(isFileWorker).forEach((reg) => reg.unregister()))
    .catch(() => {});
};

// The name in Storage: the path's last part.
const storedName = (url) => {
  try {
    return decodeURIComponent(url.slice(BASE.length + 3).split('?')[0]).split('/').pop();
  } catch (e) {
    return '';
  }
};

// The file's name for the address: the one the screen shows, with ".pdf" when
// the stored file has it and the shown name doesn't.
const pdfName = (url, name) => {
  const stored = storedName(url);
  let out = String(name || stored || '').replace(/[\\/]+/g, '_').trim();
  if (!/\.pdf$/i.test(out) && /\.pdf$/i.test(stored)) out += '.pdf';
  return /\.pdf$/i.test(out) ? out : '';
};

// Where a file's link takes the tab.
export const fileTabUrl = (url, name) => {
  if (!isStored(url) || !supported()) return url;
  const fileName = pdfName(url, name);
  if (!fileName) return url;
  registerFileWorker();
  return `${SCOPE}${encodeURIComponent(fileName)}?u=${encodeURIComponent(url)}`;
};

export const openFileTab = (url, name) => {
  if (!url) return;
  window.open(fileTabUrl(url, name), '_blank');
};

// The download icons: the file saved with its name. Where Storage can't be
// read from the page, it opens in a tab instead. On a phone it opens in a tab
// right away, as before: a save after the download, or a tab opened then, is
// no longer the tap, and the phone can block it.
export const saveStoredFile = async ({ url, name }) => {
  if (!url) return;
  if (touchDevice()) {
    openFileTab(url, name);
    return;
  }
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`download-failed:${response.status}`);
    saveAs(await response.blob(), name || storedName(url) || 'file');
  } catch (e) {
    openFileTab(url, name);
  }
};

// /archivo/... reached the app: the worker wasn't answering yet (the first PDF
// on this browser, or a browser without workers). Install it and load the
// address again, once; otherwise (and on phones) go to the Storage link.
// Even when the worker already controls this tab: it took it over while the
// app loaded (the first PDF, opened from a page that had just installed it),
// so this load wasn't its; that one went to the Storage link (the old name).
export const isFileRoute = () => typeof window !== 'undefined' && window.location.pathname.startsWith(SCOPE);

export const openFileRoute = async () => {
  const src = new URLSearchParams(window.location.search).get('u') || '';
  if (!isStored(src)) {
    window.location.replace('/');
    return;
  }
  let retried = true;
  try {
    retried = sessionStorage.getItem(RETRY_KEY) === window.location.href;
    sessionStorage.setItem(RETRY_KEY, window.location.href);
  } catch (e) {
    retried = true;
  }
  if (supported() && !retried) {
    const reg = await registerFileWorker();
    if (reg) {
      const ready = await Promise.race([
        navigator.serviceWorker.ready.then(() => true),
        new Promise((resolve) => setTimeout(() => resolve(false), 4000)),
      ]);
      if (ready) {
        window.location.reload();
        return;
      }
    }
  }
  window.location.replace(src);
};

export const isFileWorker = (reg) => Boolean(reg && reg.scope && new URL(reg.scope).pathname === SCOPE);
